import React from 'react';
import styled, {keyframes} from 'styled-components';
import {Layout, Typography, Row, Col} from 'antd';
import {CheckCircleOutlined, RocketOutlined, StarOutlined} from '@ant-design/icons';
import {CenteredContainer as Container} from '../fields/ContainerFields';
import {AsyncWishListForm} from './WishListForm';

const {Title, Paragraph} = Typography;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

// Styled Components
const WaitlistContainer = styled.section`
  id: waitlist-section;
  background: ${props => props.theme.colors?.neutral?.[900] || '#171717'};
  padding: ${props => props.theme.spacing?.[20] || '5rem'}
    ${props => props.theme.spacing?.[6] || '1.5rem'};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 30%, rgba(249, 115, 22, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[12] || '3rem'}
      ${props => props.theme.spacing?.[4] || '1rem'};
  }
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const IconWrapper = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing?.[8] || '2rem'};
  animation: ${fadeInUp} 1s ease-out;
`;

const MainIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${props =>
    props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${props => props.theme.spacing?.[6] || '1.5rem'};
  box-shadow: ${props =>
    props.theme.boxShadow?.xl ||
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'};
  animation: ${float} 3s ease-in-out infinite;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border-radius: 50%;
    background: ${props =>
      props.theme.gradients?.primary || 'linear-gradient(135deg, #e22784 0%, #fc673d 100%)'};
    opacity: 0.3;
    animation: ${pulse} 2s ease-in-out infinite;
    z-index: -1;
  }

  .anticon {
    font-size: 3rem;
    color: white;
  }
`;

const SectionTitle = styled(Title)`
  && {
    font-family: ${props =>
      props.theme.typography?.fontFamily?.heading ||
      "'Hanson Bold', 'Inter', system-ui, sans-serif"};
    font-size: ${props => props.theme.typography?.fontSize?.['4xl'] || '2.25rem'};
    font-weight: ${props => props.theme.typography?.fontWeight?.black || '900'};
    color: ${props => props.theme.colors?.neutral?.[50] || '#fafafa'};
    text-align: center;
    margin-bottom: ${props => props.theme.spacing?.[4] || '1rem'};
    animation: ${fadeInUp} 1s ease-out 0.2s both;

    @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
      font-size: ${props => props.theme.typography?.fontSize?.['3xl'] || '1.875rem'};
    }
  }
`;

const SectionSubtitle = styled(Paragraph)`
  && {
    font-size: ${props => props.theme.typography?.fontSize?.lg || '1.125rem'};
    color: ${props => props.theme.colors?.neutral?.[400] || '#a3a3a3'};
    text-align: center;
    margin-bottom: ${props => props.theme.spacing?.[12] || '3rem'};
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: ${props => props.theme.typography?.lineHeight?.relaxed || '1.625'};
    animation: ${fadeInUp} 1s ease-out 0.4s both;
  }
`;

const FormWrapper = styled.div`
  background: ${props => props.theme.colors?.glass?.light || 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(20px);
  border-radius: ${props => props.theme.borderRadius?.['3xl'] || '1.5rem'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: ${props => props.theme.spacing?.[10] || '2.5rem'};
  box-shadow: ${props => props.theme.boxShadow?.glass || '0 8px 32px 0 rgba(31, 38, 135, 0.37)'};
  animation: ${fadeInUp} 1s ease-out 0.6s both;
  transition: all ${props => props.theme.animation?.duration?.normal || '300ms'}
    ${props => props.theme.animation?.easing?.ease || 'cubic-bezier(0.4, 0, 0.2, 1)'};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props =>
      props.theme.boxShadow?.glassHover || '0 8px 32px 0 rgba(31, 38, 135, 0.5)'};
  }

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    padding: ${props => props.theme.spacing?.[8] || '2rem'};
  }
`;

const BenefitsList = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme.spacing?.[8] || '2rem'};
  margin-top: ${props => props.theme.spacing?.[12] || '3rem'};
  flex-wrap: wrap;

  @media (max-width: ${props => props.theme.breakpoints?.md || '768px'}) {
    gap: ${props => props.theme.spacing?.[6] || '1.5rem'};
    margin-top: ${props => props.theme.spacing?.[8] || '2rem'};
  }
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing?.[3] || '0.75rem'};
  color: ${props => props.theme.colors?.neutral?.[300] || '#d4d4d4'};
  font-size: ${props => props.theme.typography?.fontSize?.base || '1rem'};
  font-weight: ${props => props.theme.typography?.fontWeight?.medium || '500'};
  animation: ${fadeInUp} 1s ease-out;
  animation-delay: ${props => props.delay || '0s'};
  animation-fill-mode: both;

  .anticon {
    color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
    font-size: 1.25rem;
  }

  @media (max-width: ${props => props.theme.breakpoints?.sm || '640px'}) {
    font-size: ${props => props.theme.typography?.fontSize?.sm || '0.875rem'};
  }
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;

const FloatingIcon = styled.div`
  position: absolute;
  color: ${props => props.theme.colors?.secondary?.[500] || '#f97316'};
  opacity: 0.1;
  animation: ${float} ${props => props.duration || '4s'} ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  font-size: 2rem;
`;

const WishList = () => {
  return (
    <WaitlistContainer id="waitlist-section">
      <FloatingElements>
        <FloatingIcon
          style={{
            top: '20%',
            left: '10%',
          }}
          duration="6s"
          delay="0s"
        >
          <StarOutlined />
        </FloatingIcon>
        <FloatingIcon
          style={{
            top: '60%',
            right: '15%',
          }}
          duration="8s"
          delay="2s"
        >
          <RocketOutlined />
        </FloatingIcon>
        <FloatingIcon
          style={{
            bottom: '30%',
            left: '20%',
          }}
          duration="7s"
          delay="4s"
        >
          <CheckCircleOutlined />
        </FloatingIcon>
      </FloatingElements>

      <ContentWrapper>
        <IconWrapper>
          <MainIcon>
            <CheckCircleOutlined />
          </MainIcon>
        </IconWrapper>

        <SectionTitle level={2}>Stay Updated</SectionTitle>

        <SectionSubtitle>
          Be the first to know when CLLCTVE launches. Join our waitlist to get early access and
          exclusive opportunities to connect with top brands.
        </SectionSubtitle>

        <Row justify="center">
          <Col xs={24} md={16} lg={12}>
            <FormWrapper>
              <AsyncWishListForm />
            </FormWrapper>
          </Col>
        </Row>

        <BenefitsList>
          <BenefitItem delay="0.8s">
            <RocketOutlined />
            <span>Early Access</span>
          </BenefitItem>
          <BenefitItem delay="1s">
            <StarOutlined />
            <span>Exclusive Opportunities</span>
          </BenefitItem>
          <BenefitItem delay="1.2s">
            <CheckCircleOutlined />
            <span>Priority Support</span>
          </BenefitItem>
        </BenefitsList>
      </ContentWrapper>
    </WaitlistContainer>
  );
};

export default WishList;
